/**
 * FinancialService
 * 
 * This service handles all financial-related operations in the application.
 * It provides methods for managing fixed fees, billed time, agent disbursements,
 * and other financial data. The service supports operations for both individual
 * matters and system-wide financial management.
 * 
 * @class
 */

import HttpService from './HttpService';
import {
    pathAssignedFixedFeesByMatter,
    pathAssignedFixedFees,
    pathFixedFees,
    pathAddFixedFees,
    pathGetBilledTimeByMatter,
    pathGetAllBilledTime,
    pathGetBilledTimeByMonth,
    pathEditBilledTime,
    pathAgentDisbursementsByMatter,
    pathAgentDisbursements,
    pathAllAssignedFinancialData,
    pathGetAllBilledTimeByMonth,
    pathBillableTargets,
    pathMyBilledTime,
    pathOtherBilling,
    pathOtherBillingByMatter,
    pathOtherBillingById,
    pathFixedDisbursements,
    pathFixedDisbursementById,
    pathBilledTime,
    pathLogTime,
    pathAssignedFixedFeesByClientId,
    pathBilledTimeByClientId,
    pathCostOfWorkTime,
    pathCostOfWorkTimeByMatter,
    pathFieldRateTime,
    pathFieldRateTimeByMatter,
    pathDeleteBilledTime,
    pathDeleteAssignedFixedFee,
} from '@frontend/__settings__/septSoftwarePaths';

// shared HttpService instance
const http = new HttpService();

class FinancialService {
    /**
     * Retrieves fixed fees assigned to a specific matter
     * 
     * @param {string} matterId - ID of the matter
     * @returns {Promise<Array>} Array of assigned fixed fees
     * @throws {Error} If the API request fails
     */
    static async getAssignedFixedFees(matterId: string) {
        try {
            const data = await http.get(`${pathAssignedFixedFeesByMatter(matterId)}`);
            return data;
        } catch (error) {
            console.error('Error fetching assigned fixed fees:', error);
            return [];
        }
    }

    /**
     * Retrieves assigned fixed fees across all matters for a client
     *
     * @param {string} clientId - ID of the client
     * @returns {Promise<Array>} Array of assigned fixed fee records
     * @throws {Error} If the API request fails
     */
    static async getAssignedFixedFeesByClientId(clientId: string) {
        try {
          const response = await http.get(`${pathAssignedFixedFeesByClientId(clientId)}`);
          return response || [];
        } catch (error) {
          console.error(`Error fetching assigned fixed fees for client ${clientId}:`, error);
          return [];
        }
    }

    

    /**
     * Retrieves all fixed fees from the system
     * 
     * @returns {Promise<Array>} Array of fixed fee objects
     * @throws {Error} If the API request fails
     */
    static async getFixedFees() {
        try {
            const data = await http.get(`${pathFixedFees}`);
            return data || [];
        } catch (error) {
            console.error('Error fetching fixed fees:', error);
            return [];  // Return empty array on error
        }
    }

    /**
     * Assigns fixed fees to a matter
     * 
     * @param {Object} formData - Fixed fee assignment data
     * @param {string} formData.matterId - ID of the matter
     * @param {string} formData.feeId - ID of the fixed fee
     * @param {number} formData.amount - Amount to assign
     * 
     * @returns {Promise<Object>} Created fixed fee assignment
     * @throws {Error} If the API request fails
     */
    static async addAssignedFixedFees(formData: any) {
        try {
            const data = await http.post(`${pathAssignedFixedFees}`, formData);
            return data;
        } catch (error) {
            console.error('Error adding assigned fixed fees:', error);
            throw error;
        }
    }

    /**
     * Creates a new fixed fee in the system
     * 
     * @param {Object} formData - Fixed fee data
     * @param {string} formData.name - Name of the fixed fee
     * @param {number} formData.amount - Amount of the fixed fee
     * @param {string} [formData.description] - Description of the fixed fee
     * 
     * @returns {Promise<Object>} Created fixed fee object
     * @throws {Error} If the API request fails
     */
    static async addFixedFee(formData: any) {
        try {
            const data = await http.post(`${pathAddFixedFees}`, formData);
            return data;
        } catch (error) {
            console.error('Error adding fixed fee:', error);
            throw error;
        }
    }

    /**
     * Retrieves billed time entries for a specific matter
     * 
     * @param {string} matterId - ID of the matter
     * @returns {Promise<Array>} Array of billed time entries
     * @throws {Error} If the API request fails
     */
    static async getBilledTime(matterId: string) {
        try {
            const data = await http.get(`${pathGetBilledTimeByMatter(matterId)}`);
            return data;
        } catch (error) {
            console.error('Error fetching billed time data:', error);
            return [];
        }
    }

    /**
     * Retrieves all billed time entries from the system
     * 
     * @returns {Promise<Array>} Array of all billed time entries
     * @throws {Error} If the API request fails
     */
    static async getAllBilledTime() {
        try {
            const data = await http.get(`${pathGetAllBilledTime}`);
            return data || [];
        } catch (error) {
            console.error('Error fetching all billed time data:', error);
            return [];  // Return empty array on error
        }
    }

    /**
     * Retrieves billed time entries for a specific matter and month
     * 
     * @param {string} matterId - ID of the matter
     * @param {string} month - Month to filter (format: YYYY-MM)
     * @returns {Promise<Array>} Array of billed time entries
     * @throws {Error} If the API request fails
     */
    static async getBilledTimeByMonth(matterId: string, month: string) {
        try {
            const data = await http.get(`${pathGetBilledTimeByMonth(matterId, month)}`);
            return data;
        } catch (error) {
            console.error(`Error fetching billed times for matter ID ${matterId} in month ${month}:`, error);
            throw error;
        }
    }
    
    /**
     * Adds a new billed time entry
     * 
     * @param {Object} formData - Billed time data
     * @param {string} formData.matterId - ID of the matter
     * @param {string} formData.employeeId - ID of the employee
     * @param {number} formData.hours - Number of hours billed
     * @param {string} formData.description - Description of the work
     * @param {Date} formData.date - Date of the work
     * 
     * @returns {Promise<Object>} Created billed time entry
     * @throws {Error} If the API request fails
     */
    static async addBilledTime(formData: any) {
        try {
            const data = await http.post(`${pathBilledTime}`, formData);
            return data;
        } catch (error) {
            console.error('Error adding billed time entry:', error);
            throw error;
        }
    }

    /**
     * Logs a unified time entry across all three rate tables (billed_time,
     * costofwork_time, fieldrate_time). The backend resolves each rate from
     * company_rates and calculates each amount automatically.
     *
     * @param formData - { time_recorded, date_recorded, notes, matterid, employeeid, tax_percent?, Type? }
     * @returns Created entry IDs and calculated amounts for all three tables
     */
    static async logTime(formData: any) {
        try {
            const data = await http.post(`${pathLogTime}`, formData);
            return data;
        } catch (error) {
            console.log('Error logging time entry:', error);
            throw error;
        }
    }

    static async getCostOfWorkTime(matterId: string) {
        try {
            return await http.get(`${pathCostOfWorkTimeByMatter(matterId)}`);
        } catch (error) {
            console.log('Error fetching cost of work time:', error);
            return [];
        }
    }

    static async getAllCostOfWorkTime() {
        try {
            return await http.get(`${pathCostOfWorkTime}`);
        } catch (error) {
            console.log('Error fetching all cost of work time:', error);
            return [];
        }
    }

    static async getFieldRateTime(matterId: string) {
        try {
            return await http.get(`${pathFieldRateTimeByMatter(matterId)}`);
        } catch (error) {
            console.log('Error fetching field rate time:', error);
            return [];
        }
    }

    static async getAllFieldRateTime() {
        try {
            return await http.get(`${pathFieldRateTime}`);
        } catch (error) {
            console.log('Error fetching all field rate time:', error);
            return [];
        }
    }

    /**
     * Retrieves billed time entries for a specific client
     * 
     * @param {string} clientId - ID of the client
     * @returns {Promise<Array>} Array of billed time entries across all client matters
     * @throws {Error} If the API request fails
     */
    static async getBilledTimeByClientId(clientId: string) {
        try {
          const response = await http.get(`${pathBilledTimeByClientId(clientId)}`);
          return response || [];
        } catch (error) {
            console.error(`Error fetching billed time for client ${clientId}:`, error);
            return [];
        }
    }

    /**
     * Updates an existing billed time entry
     * 
     * @param {string} btId - ID of the billed time entry
     * @param {Object} formData - Updated billed time data
     * @returns {Promise<Object>} Updated billed time entry
     * @throws {Error} If the API request fails
     */
    static async updateBilledTime(btId: string, formData: any) {
        const data = await http.put(`${pathEditBilledTime(btId)}`, formData);
        return data;
    }

    static async deleteBilledTime(btId: string) {
        const data = await http.delete(`${pathDeleteBilledTime(btId)}`);
        return data;
    }

    static async deleteAssignedFixedFee(assignedId: string) {
        const data = await http.delete(`${pathDeleteAssignedFixedFee(assignedId)}`);
        return data;
    }

    /**
     * Retrieves agent disbursements for a specific matter
     * 
     * @param {string} matterId - ID of the matter
     * @returns {Promise<Array>} Array of agent disbursements
     * @throws {Error} If the API request fails
     */
    static async getAgentDisbursements(matterId: string) {
        try {
            const data = await http.get(`${pathAgentDisbursementsByMatter(matterId)}`);
            return data;
        } catch (error) {
            console.error('Error fetching agent disbursements:', error);
            return [];
        }
    }

    /**
     * Retrieves all agent disbursements from the system
     * 
     * @returns {Promise<Array>} Array of all agent disbursements
     * @throws {Error} If the API request fails
     */
    static async getGeneralAgentDisbursements() {
        try {
            const data = await http.get(`${pathAgentDisbursements}`);
            return data;
        } catch (error) {
            console.error('Error fetching agent disbursements:', error);
            return [];
        }
    }

    /**
     * Adds a new agent disbursement
     * 
     * @param {Object} formData - Agent disbursement data
     * @param {string} formData.matterId - ID of the matter
     * @param {string} formData.agentId - ID of the agent
     * @param {number} formData.amount - Amount of the disbursement
     * @param {string} formData.description - Description of the disbursement
     * 
     * @returns {Promise<Object>} Created agent disbursement
     * @throws {Error} If the API request fails
     */
    static async addAgentDisbursement(formData: any) {
        try {
            const data = await http.post(`${pathAgentDisbursements}`, formData);
            return data;
        } catch (error) {
            console.error('Error adding agent disbursement:', error);
            throw error;
        }
    }

    /**
     * Retrieves all assigned financial data from the system
     * 
     * @returns {Promise<Array>} Array of assigned financial data
     * @throws {Error} If the API request fails
     */
    static async getAllAssignedFinancialData() {
        try {
                        const data = await http.get(`${pathAllAssignedFinancialData}`);
                        return data;
        } catch (error) {
          console.log("Error fetching all assigned financial data:", error);
          return [];
        }
    }

    /**
     * Retrieves all billed time entries for a specific month
     * 
     * @param {string} month - Month to filter (format: YYYY-MM)
     * @returns {Promise<Array>} Array of billed time entries
     * @throws {Error} If the API request fails
     */
    static async getAllBilledTimeByMonth(month: string) {
        try {
            const data = await http.get(`${pathGetAllBilledTimeByMonth(month)}`);
            return data;
        } catch (error) {
            console.error(`Error fetching all billed times for month ${month}:`, error);
            throw error;
        }
    }

    /**
     * Retrieves billable targets for a specific employee
     * 
     * @param {string} employeeId - ID of the employee
     * @returns {Promise<Object>} Billable targets data
     * @throws {Error} If the API request fails
     */
    static async getBillableTargetsByEmployeeId(employeeId: string) {
        try {
            const data = await http.get(`${pathBillableTargets(employeeId)}`);
            return data;
        } catch (error) {
            console.error('Error fetching billable targets:', error);
            return [];
        }
    }

    /**
     * Retrieves billed time entries for a specific employee
     * 
     * @param {string} employeeId - ID of the employee
     * @returns {Promise<Array>} Array of billed time entries
     * @throws {Error} If the API request fails
     */
    static async getBilledTimeByEmployeeId(employeeId: string) {
        try {
            const data = await http.get(`${pathMyBilledTime(employeeId)}`);
            return data;
        } catch (error) {
            console.error('Error fetching billed time by employee ID:', error);
            return [];
        }
    }

    /**
     * Retrieves all other billing entries from the system
     * 
     * @returns {Promise<Array>} Array of other billing entries
     * @throws {Error} If the API request fails
     */
    static async getAllOtherBilling() {
        try {
            const data = await http.get(`${pathOtherBilling}`);
            return data || [];
        } catch (error) {
            console.error('Error fetching all other billing entries:', error);
            return [];  // Return empty array on error
        }
    }

    /**
     * Retrieves other billing entries for a specific matter
     * 
     * @param {string} matterId - ID of the matter
     * @returns {Promise<Array>} Array of other billing entries
     * @throws {Error} If the API request fails
     */
    static async getOtherBillingByMatter(matterId: string) {
        try {
            const data = await http.get(`${pathOtherBillingByMatter(matterId)}`);
            return data;
        } catch (error) {
            console.error('Error fetching other billing entries by matter ID:', error);
            return [];
        }
    }

    /**
     * Adds a new other billing entry
     * 
     * @param {Object} formData - Other billing data
     * @param {string} formData.matterId - ID of the matter
     * @param {number} formData.amount - Amount of the billing
     * @param {string} formData.description - Description of the billing
     * 
     * @returns {Promise<Object>} Created other billing entry
     * @throws {Error} If the API request fails
     */
    static async addOtherBilling(formData: any) {
        try {
            const data = await http.post(`${pathOtherBilling}`, formData);
            return data;
        } catch (error) {
            console.error('Error adding other billing entry:', error);
            throw error;
        }
    }

    /**
     * Updates an existing other billing entry
     * 
     * @param {string} obId - ID of the other billing entry
     * @param {Object} formData - Updated other billing data
     * @returns {Promise<Object>} Updated other billing entry
     * @throws {Error} If the API request fails
     */
    static async updateOtherBilling(obId: string, formData: any) {
        try {
            const data = await http.put(`${pathOtherBillingById(obId)}`, formData);
            return data;
        } catch (error) {
            console.error('Error updating other billing entry:', error);
            throw error;
        }
    }

    /**
     * Deletes an other billing entry
     * 
     * @param {string} obId - ID of the other billing entry to delete
     * @returns {Promise<Object>} Response indicating success or failure
     * @throws {Error} If the API request fails
     */
    static async deleteOtherBilling(obId: string) {
        try {
            const data = await http.delete(`${pathOtherBillingById(obId)}`);
            return data;
        } catch (error) {
            console.error('Error deleting other billing entry:', error);
            throw error;
        }
    }

    /**
     * Retrieves all fixed disbursements from the system
     * 
     * @returns {Promise<Array>} Array of fixed disbursements
     * @throws {Error} If the API request fails
     */
    static async getFixedDisbursements() {
        try {
            const data = await http.get(`${pathFixedDisbursements}`);
            return data;
        } catch (error) {
            console.error('Error fetching fixed disbursements:', error);
            return [];
        }
    }

    /**
     * Adds a new fixed disbursement
     * 
     * @param {Object} formData - Fixed disbursement data
     * @param {string} formData.name - Name of the disbursement
     * @param {number} formData.amount - Amount of the disbursement
     * @param {string} [formData.description] - Description of the disbursement
     * 
     * @returns {Promise<Object>} Created fixed disbursement
     * @throws {Error} If the API request fails
     */
    static async addFixedDisbursement(formData: any) {
        try {
            const data = await http.post(`${pathFixedDisbursements}`, formData);
            return data;
        } catch (error) {
            console.error('Error adding fixed disbursement:', error);
            throw error;
        }
    }

    /**
     * Updates an existing fixed disbursement
     * 
     * @param {string} id - ID of the fixed disbursement
     * @param {Object} formData - Updated fixed disbursement data
     * @returns {Promise<Object>} Updated fixed disbursement
     * @throws {Error} If the API request fails
     */
    static async updateFixedDisbursement(id: string, formData: any) {
        try {
            const data = await http.put(`${pathFixedDisbursementById(id)}`, formData);
            return data;
        } catch (error) {
            console.error('Error updating fixed disbursement:', error);
            throw error;
        }
    }

    /**
     * Deletes a fixed disbursement
     * 
     * @param {string} id - ID of the fixed disbursement to delete
     * @returns {Promise<Object>} Response indicating success or failure
     * @throws {Error} If the API request fails
     */
    static async deleteFixedDisbursement(id: string) {
        try {
            const data = await http.delete(`${pathFixedDisbursementById(id)}`);
            return data;
        } catch (error) {
            console.error('Error deleting fixed disbursement:', error);
            throw error;
        }
    }
}

export default FinancialService;
